import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';

const PMP_FONT = "'abcNormal','Inter','Arial',sans-serif";
const PMP_FONT_FACE = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;

const ProductManagementPanel = () => {
  const { toast, confirm } = useNotification();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subtitle: '',
    short_description: '',
    full_description: '',
    price: 0,
    promo_price: null,
    discount_percentage: null,
    discount_codes: [],
    is_active: true,
    benefits: [],
    media_gallery: []
  });
  const [newCode, setNewCode] = useState('');
  const [newCodePercentage, setNewCodePercentage] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadProduct();
  }, []);

  const loadProduct = async () => {
    try {
      const response = await axios.get(`${API}/admin/product`);
      setProduct(response.data);
      setFormData({
        name: response.data.name || '',
        subtitle: response.data.subtitle || '',
        short_description: response.data.short_description || '',
        full_description: response.data.full_description || '',
        price: response.data.price || 0,
        promo_price: response.data.promo_price || null,
        discount_percentage: response.data.discount_percentage || null,
        discount_codes: response.data.discount_codes || [],
        is_active: response.data.is_active !== undefined ? response.data.is_active : true,
        benefits: response.data.benefits || [],
        media_gallery: response.data.media_gallery || []
      });
      if (response.data.image_url) {
        setImagePreview(`${API}/files/${response.data.image_url}`);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('productManagement.toast.selectValidImage', 'Por favor selecciona una imagen válida'));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('productManagement.toast.imageMax2MB', 'La imagen debe ser menor a 2MB'));
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', imageFile);

    try {
      const response = await axios.post(`${API}/admin/product/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.filename;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(t('productManagement.toast.imageUploadError', 'Error al subir la imagen'));
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    // Validations
    if (!formData.name.trim()) {
      toast.error(t('productManagement.toast.nameRequired', 'El nombre del producto es obligatorio'));
      return;
    }

    if (!formData.price || formData.price <= 0) {
      toast.error(t('productManagement.toast.priceMustBePositive', 'El precio debe ser mayor a 0'));
      return;
    }

    if (formData.promo_price && formData.promo_price >= formData.price) {
      toast.error(t('productManagement.toast.promoPriceInvalid', 'El precio promocional debe ser menor al precio original'));
      return;
    }

    setSaving(true);

    try {
      // Upload image if new image selected
      let imageUrl = product?.image_url;
      if (imageFile) {
        const uploadedFilename = await uploadImage();
        if (uploadedFilename) {
          imageUrl = uploadedFilename;
        }
      }

      // Prepare update data
      const updateData = {
        ...formData,
        image_url: imageUrl,
        price: Math.round(parseFloat(formData.price)),
        promo_price: formData.promo_price ? Math.round(parseFloat(formData.promo_price)) : null,
        discount_percentage: formData.discount_percentage ? parseFloat(formData.discount_percentage) : null
      };

      const response = await axios.put(`${API}/admin/product`, updateData);
      
      if (response.data.success) {
        toast.success(t('productManagement.toast.productUpdated', 'Producto actualizado exitosamente'));
        await loadProduct();
        setImageFile(null);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(t('productManagement.toast.saveError', 'Error al guardar el producto'));
    } finally {
      setSaving(false);
    }
  };

  const addDiscountCode = () => {
    if (!newCode.trim()) return;

    const percentage = parseFloat(newCodePercentage);
    if (isNaN(percentage) || percentage <= 0 || percentage > 100) {
      toast.error(t('productManagement.toast.invalidPercentage', 'Por favor ingresa un porcentaje válido entre 1 y 100'));
      return;
    }

    const codeUpper = newCode.trim().toUpperCase();

    // Check if code already exists (handling both old string format and new object format)
    const codeExists = formData.discount_codes.some(dc => {
      if (typeof dc === 'string') return dc === codeUpper;
      return dc.code === codeUpper;
    });

    if (codeExists) {
      toast.error(t('productManagement.toast.codeExists', 'Este código ya existe'));
      return;
    }

    setFormData(prev => ({
      ...prev,
      discount_codes: [...prev.discount_codes, { code: codeUpper, percentage: percentage }]
    }));
    setNewCode('');
    setNewCodePercentage('');
  };

  const removeDiscountCode = (code) => {
    setFormData(prev => ({
      ...prev,
      discount_codes: prev.discount_codes.filter(dc => {
        if (typeof dc === 'string') return dc !== code;
        return dc.code !== code;
      })
    }));
  };

  const addBenefit = () => {
    if (!newBenefit.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      benefits: [...prev.benefits, newBenefit.trim()]
    }));
    setNewBenefit('');
  };

  const removeBenefit = (index) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error(t('productManagement.toast.selectValidMedia', 'Por favor selecciona una imagen o video válido'));
      return;
    }

    // Validate file size
    const maxSize = isImage ? 5 * 1024 * 1024 : 50 * 1024 * 1024; // 5MB for images, 50MB for videos
    const maxSizeLabel = isImage ? '5MB' : '50MB';
    if (file.size > maxSize) {
      toast.error(t('productManagement.toast.fileMaxSize', `El archivo debe ser menor a ${maxSizeLabel}`).replace('{size}', maxSizeLabel));
      return;
    }

    setUploadingMedia(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/product/media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Reload product to get updated gallery
        await loadProduct();
        toast.success(isImage ? t('productManagement.toast.imageUploaded', 'Imagen subido exitosamente') : t('productManagement.toast.videoUploaded', 'Video subido exitosamente'));
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error(t('productManagement.toast.fileUploadError', 'Error al subir el archivo'));
    } finally {
      setUploadingMedia(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!(await confirm(t('productManagement.confirm.deleteFile', '¿Estás seguro de eliminar este archivo?'), { confirmLabel: t('productManagement.delete', 'Eliminar') }))) return;

    try {
      const response = await axios.delete(`${API}/admin/product/media/${mediaId}`);

      if (response.data.success) {
        // Reload product to get updated gallery
        await loadProduct();
        toast.success(t('productManagement.toast.fileDeleted', 'Archivo eliminado exitosamente'));
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error(t('productManagement.toast.fileDeleteError', 'Error al eliminar el archivo'));
    }
  };

  const handleReorderMedia = async (fromIndex, toIndex) => {
    const newGallery = [...formData.media_gallery];
    const [movedItem] = newGallery.splice(fromIndex, 1);
    newGallery.splice(toIndex, 0, movedItem);

    // Update local state immediately for smooth UX
    setFormData(prev => ({
      ...prev,
      media_gallery: newGallery
    }));

    try {
      const mediaOrder = newGallery.map(m => m.id);
      await axios.put(`${API}/admin/product/media/reorder`, mediaOrder);
    } catch (error) {
      console.error('Error reordering media:', error);
      // Reload to revert changes if failed
      await loadProduct();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004AAD] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('productManagement.loading', 'Cargando producto...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{PMP_FONT_FACE}</style>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.01em', fontFamily: PMP_FONT }}>{t('productManagement.title', 'Gestión de Producto')}</h1>
          <p className="text-gray-600 mt-1">{t('productManagement.subtitle', 'Edita la información del producto visible en la página de compra')}</p>
        </div>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          {showPreview ? t('productManagement.hidePreview', 'Ocultar Vista Previa') : t('productManagement.showPreview', 'Ver Vista Previa')}
        </button>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl shadow-md p-4 md:p-6 space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('productManagement.basicInfo', 'Información Básica')}</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productManagement.productName', 'Nombre del Producto *')}
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
              placeholder={t('productManagement.productNamePlaceholder', 'Ej: Tarjeta Inteligente Contact')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productManagement.subtitleLabel', 'Subtítulo')}
            </label>
            <input
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
              placeholder={t('productManagement.subtitlePlaceholder', 'Ej: La herramienta definitiva para equipos comerciales modernos')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productManagement.shortDescription', 'Descripción Breve')}
            </label>
            <textarea
              name="short_description"
              value={formData.short_description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
              placeholder={t('productManagement.shortDescriptionPlaceholder', 'Descripción corta del producto (2-3 líneas)')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productManagement.fullDescription', 'Descripción Completa')}
            </label>
            <textarea
              name="full_description"
              value={formData.full_description}
              onChange={handleInputChange}
              rows={5}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
              placeholder={t('productManagement.fullDescriptionPlaceholder', 'Descripción detallada del producto')}
            />
          </div>
        </div>

        {/* Image Upload - Backwards Compatibility */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('productManagement.mainImageSection', 'Imagen del Producto (Principal)')}</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('productManagement.mainImageLabel', 'Imagen Principal (JPG/PNG, máx 2MB)')}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {imagePreview && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">{t('productManagement.preview', 'Vista Previa:')}</p>
              <div className="w-48 h-48 border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          )}
        </div>

        {/* Media Gallery - Multiple Images/Videos */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('productManagement.mediaGallery', 'Galería de Medios')}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {t('productManagement.mediaGallerySub', 'Agrega múltiples imágenes y videos para mostrar en la página del producto')}
              </p>
            </div>
            <label className="px-4 py-2 bg-[#004AAD] text-white rounded-lg hover:bg-[#5170FF] transition-colors cursor-pointer inline-flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {uploadingMedia ? t('productManagement.uploading', 'Subiendo...') : t('productManagement.addFile', 'Agregar Archivo')}
              <input
                type="file"
                accept="image/*,video/*"
                onChange={handleMediaUpload}
                disabled={uploadingMedia}
                className="hidden"
              />
            </label>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">{t('productManagement.acceptedFormats', 'Formatos aceptados:')}</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>{t('productManagement.images', 'Imágenes:')}</strong> JPG, PNG, WebP ({t('productManagement.max', 'máx')} 5MB)</li>
                  <li><strong>{t('productManagement.videos', 'Videos:')}</strong> MP4, WebM, MOV ({t('productManagement.max', 'máx')} 50MB)</li>
                </ul>
                <p className="mt-2">{t('productManagement.filesOrderNote', 'Los archivos se mostrarán en el orden que aparecen aquí. Arrastra para reordenar.')}</p>
              </div>
            </div>
          </div>

          {/* Media Gallery Grid */}
          {formData.media_gallery && formData.media_gallery.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {formData.media_gallery.map((media, index) => (
                <div
                  key={media.id}
                  className="relative group border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 hover:border-[#004AAD] transition-all"
                >
                  {/* Media Preview */}
                  <div className="aspect-square relative">
                    {media.type === 'image' ? (
                      <img
                        src={media.url}
                        alt={media.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="relative w-full h-full">
                        <video
                          src={media.url}
                          className="w-full h-full object-cover"
                          muted
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                          <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Order Badge */}
                  <div className="absolute top-2 left-2 bg-[#004AAD] text-white text-xs font-bold px-2 py-1 rounded">
                    #{index + 1}
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeleteMedia(media.id)}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t('productManagement.delete', 'Eliminar')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Reorder Buttons */}
                  <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {index > 0 && (
                      <button
                        onClick={() => handleReorderMedia(index, index - 1)}
                        className="bg-white hover:bg-gray-100 p-1.5 rounded shadow-md"
                        title={t('productManagement.moveLeft', 'Mover izquierda')}
                      >
                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}
                    {index < formData.media_gallery.length - 1 && (
                      <button
                        onClick={() => handleReorderMedia(index, index + 1)}
                        className="bg-white hover:bg-gray-100 p-1.5 rounded shadow-md"
                        title={t('productManagement.moveRight', 'Mover derecha')}
                      >
                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Filename */}
                  <div className="p-2 bg-white border-t border-gray-200">
                    <p className="text-xs text-gray-600 truncate" title={media.filename}>
                      {media.filename || `${media.type}_${index + 1}`}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {media.type === 'image' ? t('productManagement.image', 'Imagen') : t('productManagement.video', 'Video')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-600 font-medium">{t('productManagement.noMediaInGallery', 'No hay medios en la galería')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('productManagement.clickAddFile', 'Haz clic en "Agregar Archivo" para subir imágenes o videos')}</p>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('productManagement.pricing', 'Precio y Descuentos')}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('productManagement.price', 'Precio original (CLP) *')}
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
                placeholder="26990"
                min="0"
                step="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('productManagement.promoPrice', 'Precio promocional (CLP)')}
              </label>
              <input
                type="number"
                name="promo_price"
                value={formData.promo_price || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
                placeholder="20990"
                min="0"
                step="1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('productManagement.promoPriceHint', 'Precio exacto a cobrar. El % de descuento se calcula solo. Tiene prioridad sobre el descuento (%) de abajo.')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('productManagement.discount', 'Descuento (%)')}
              </label>
              <input
                type="number"
                name="discount_percentage"
                value={formData.discount_percentage || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
                placeholder="10"
                min="0"
                max="100"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('productManagement.discountHint', 'Alternativa al precio promocional. Solo se usa si el precio promocional está vacío.')}
              </p>
            </div>
          </div>
        </div>

        {/* Discount Codes */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('productManagement.discountCodes', 'Códigos de Descuento')}</h3>
          
          <div className="flex gap-2">
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
              placeholder="CODIGO2026"
            />
            <input
              type="number"
              value={newCodePercentage}
              onChange={(e) => setNewCodePercentage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addDiscountCode()}
              className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
              placeholder="10"
              min="1"
              max="100"
            />
            <span className="flex items-center text-gray-600">%</span>
            <button
              onClick={addDiscountCode}
              className="px-6 py-2 bg-[#004AAD] text-white rounded-lg hover:bg-[#5170FF] transition-colors"
            >
              {t('productManagement.add', 'Agregar')}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {formData.discount_codes.map((code, index) => {
              const codeText = typeof code === 'string' ? code : code.code;
              const codePercentage = typeof code === 'string' ? formData.discount_percentage : code.percentage;
              
              return (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-2 rounded-lg"
                >
                  <span className="font-mono font-medium">{codeText}</span>
                  <span className="text-sm text-blue-600">(-{codePercentage}%)</span>
                  <button
                    onClick={() => removeDiscountCode(codeText)}
                    className="text-blue-600 hover:text-blue-800 font-bold"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Benefits */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('productManagement.benefits', 'Beneficios')}</h3>

          <div className="flex gap-2">
            <input
              type="text"
              value={newBenefit}
              onChange={(e) => setNewBenefit(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addBenefit()}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004AAD] focus:border-transparent"
              placeholder={t('productManagement.benefitPlaceholder', 'Ej: Diseño visual personalizado con tu marca')}
            />
            <button
              onClick={addBenefit}
              className="px-6 py-2 bg-[#004AAD] text-white rounded-lg hover:bg-[#5170FF] transition-colors"
            >
              {t('productManagement.add', 'Agregar')}
            </button>
          </div>

          <div className="space-y-2">
            {formData.benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-2 bg-gray-50 p-3 rounded-lg"
              >
                <span className="flex-1 text-gray-700">{benefit}</span>
                <button
                  onClick={() => removeBenefit(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  {t('productManagement.delete', 'Eliminar')}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Active Status */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleInputChange}
            className="w-5 h-5 text-[#004AAD] rounded focus:ring-[#004AAD]"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
            {t('productManagement.activeProduct', 'Producto activo (visible en la página pública)')}
          </label>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving || uploadingImage}
            className="px-8 py-3 bg-[#004AAD] hover:bg-[#5170FF] text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving || uploadingImage ? t('productManagement.saving', 'Guardando...') : t('productManagement.saveChanges', 'Guardar Cambios')}
          </button>
        </div>
      </div>

      {/* Preview Modal - Reflejo exacto de la página /comprar */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {/* Preview Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{t('productManagement.previewTitle', 'Vista Previa - Página /comprar')}</h3>
                <p className="text-sm text-gray-600">{t('productManagement.previewLive', 'Los cambios se reflejan en tiempo real')}</p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-600 hover:text-gray-900 p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Preview Content - Igual que ComprarPage */}
            <div className="p-5 md:p-8 bg-gradient-to-b from-white via-gray-50 to-gray-100">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-12">
                {/* Product Image */}
                <div className="order-1">
                  <div className="bg-white rounded-2xl p-5 md:p-8 shadow-xl hover:shadow-2xl transition-shadow">
                    <div className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt={formData.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="text-center text-gray-400">
                          <svg className="w-24 h-24 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm">{t('productManagement.noImage', 'Sin imagen')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div className="order-2 space-y-6">
                  <div>
                    <h1 className="text-2xl lg:text-5xl font-bold text-gray-900 mb-3">
                      {formData.name || t('productManagement.previewProductName', 'Nombre del Producto')}
                    </h1>
                    <p className="text-xl text-gray-600 font-medium">
                      {formData.subtitle || t('productManagement.previewSubtitle', 'Subtítulo del producto')}
                    </p>
                  </div>

                  <p className="text-lg text-gray-700 leading-relaxed">
                    {formData.short_description || t('productManagement.previewShortDescription', 'Descripción corta del producto')}
                  </p>

                  {/* Price Display */}
                  {/* Price Display — mirrors the same promo_price-over-percentage
                      precedence and whole-CLP rounding that /comprar and the
                      backend's initiate-purchase use, so this preview never
                      shows a price/badge the real page wouldn't. */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 md:p-6 border-2 border-blue-100">
                    {(() => {
                      const originalPrice = Math.round(formData.price || 0);
                      let finalPrice = originalPrice;
                      if (formData.promo_price > 0 && formData.promo_price < originalPrice) {
                        finalPrice = Math.round(formData.promo_price);
                      } else if (formData.discount_percentage > 0) {
                        finalPrice = Math.round(originalPrice * (1 - formData.discount_percentage / 100));
                      }
                      const hasDiscount = finalPrice < originalPrice;
                      const pct = hasDiscount ? Math.round((1 - finalPrice / originalPrice) * 100) : 0;
                      return hasDiscount ? (
                        <div>
                          <p className="text-lg text-gray-500 line-through mb-1">
                            ${originalPrice.toLocaleString(dateLocale)} CLP
                          </p>
                          <p className="text-2xl md:text-4xl font-bold text-[#004AAD] mb-2 flex items-center gap-2">
                            ${finalPrice.toLocaleString(dateLocale)} CLP
                            <span className="text-xs font-bold text-white bg-gray-900 rounded-full px-2 py-1">{pct}% OFF</span>
                          </p>
                          <p className="text-sm text-red-600 font-medium">
                            🔥 {t('productManagement.limitedPromoPrice', 'Precio promocional limitado (-{pct}%)').replace('{pct}', pct)}
                          </p>
                        </div>
                      ) : (
                        <p className="text-2xl md:text-4xl font-bold text-[#004AAD]">
                          ${originalPrice.toLocaleString(dateLocale)} CLP
                        </p>
                      );
                    })()}
                  </div>

                  {/* Códigos de descuento disponibles */}
                  {formData.discount_codes.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                      <p className="text-sm font-semibold text-yellow-800 mb-2">💡 {t('productManagement.availableCodes', 'Códigos disponibles:')}</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.discount_codes.map((code, idx) => {
                          const codeText = typeof code === 'string' ? code : code.code;
                          const codePercentage = typeof code === 'string' ? formData.discount_percentage : code.percentage;
                          return (
                            <span key={idx} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg text-sm font-medium">
                              {codeText} (-{codePercentage}%)
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Trust Badge */}
                  <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{t('productManagement.trustBadge', 'Más de 50 empresas líderes ya confían en CONTACT')}</span>
                  </div>
                </div>
              </div>

              {/* Benefits Preview */}
              {formData.benefits.length > 0 && (
                <div className="mt-16">
                  <h2 className="text-xl md:text-3xl font-bold text-center text-gray-900 mb-12">
                    {t('productManagement.benefitsAndFeatures', 'Beneficios y Características')}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {formData.benefits.map((benefit, index) => {
                      const icons = ['🎨', '📱', '📊', '♻️', '🚀', '💼'];
                      return (
                        <div
                          key={index}
                          className="bg-white rounded-xl p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                        >
                          <div className="flex items-start gap-4">
                            <span className="text-3xl flex-shrink-0">{icons[index] || '✓'}</span>
                            <p className="text-gray-700 leading-relaxed">{benefit}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Order Management Panel Component

export default ProductManagementPanel;
